<template>
  <v-card class="mb-8 pa-0 elevation-2">
    <v-card-title class="text-h5 font-weight-bold pa-6 pb-4">
      <v-icon left color="primary" class="mr-3">mdi-account-multiple</v-icon>
      Audience
    </v-card-title>
    
    <v-divider></v-divider>
    
    <v-card-text class="pa-6">
      <!-- Top Stats Row -->
      <v-row class="mb-6">
        <v-col cols="12" md="6">
          <div class="stat-card stat-card-data">
            <div class="stat-left">
              <div class="stat-label">Views</div>
            </div>
            <div class="stat-center">
              <div class="text-h4 font-weight-bold stat-value">{{ formatDataUploaded }}</div>
            </div>
            <div class="stat-right" v-if="dataUploadedTrend !== null">
              <v-icon :color="dataUploadedTrendColor">{{ dataUploadedTrendIcon }}</v-icon>
              <span class="trend-value">{{ Math.abs(dataUploadedTrend) }}%</span>
            </div>
          </div>
        </v-col>
        
        <v-col cols="12" md="6">
          <div class="stat-card stat-card-sessions">
            <div class="stat-left">
              <div class="stat-label">Sessions</div>
            </div>
            <div class="stat-center">
              <div class="text-h4 font-weight-bold stat-value">{{ formatSessions }}</div>
            </div>
            <div class="stat-right" v-if="sessionsTrend !== null">
              <v-icon :color="sessionsTrendColor">{{ sessionsTrendIcon }}</v-icon>
              <span class="trend-value">{{ Math.abs(sessionsTrend) }}%</span>
            </div>
          </div>
        </v-col>
      </v-row>
      
      <!-- Bottom Charts Row -->
      <v-row>
        <v-col cols="12" md="6">
          <div class="chart-card">
            <div class="chart-header">
              <h3 class="text-h6 font-weight-bold">Problème de remontée de données GA</h3>
            </div>
            
            <!-- Dynamic Provider Charts -->
            <div v-if="gaDataIssuesProviders.length > 0">
              <div 
                v-for="provider in visibleGADataIssuesProviders" 
                :key="provider.id"
                class="provider-chart-item">
                <div class="provider-name">{{ provider.name }}</div>
                <BarChartFixed 
                  :bars="generateBarsFromData(provider.data)"
                  :tooltip-style="tooltipStyle"
                />
              </div>
              
              <!-- Expand/Collapse Button -->
              <div v-if="gaDataIssuesProviders.length > 2" class="text-center mt-3">
                <v-btn
                  text
                  small
                  color="primary"
                  @click="expandedSections.gaDataIssues = !expandedSections.gaDataIssues"
                >
                  <v-icon left small>
                    {{ expandedSections.gaDataIssues ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                  {{ expandedSections.gaDataIssues ? 'Voir moins' : `Voir ${gaDataIssuesProviders.length - 2} de plus` }}
                </v-btn>
              </div>
            </div>
            
            <!-- Loading State -->
            <div v-else-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-3 text--secondary">Chargement des données...</div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="text-center py-8 text--secondary">
              <v-icon large class="mb-3">mdi-check-circle</v-icon>
              <div>Aucun problème de remontée détecté</div>
            </div>
          </div>
        </v-col>
        
        <v-col cols="12" md="6">
          <div class="chart-card">
            <div class="chart-header">
              <h3 class="text-h6 font-weight-bold">Problème de remontée de données Wysistat</h3>
            </div>
            
            <!-- Dynamic Wysistat Charts -->
            <div v-if="wysistatDataIssuesProviders.length > 0">
              <div 
                v-for="provider in visibleWysistatDataIssuesProviders" 
                :key="provider.id"
                class="provider-chart-item">
                <div class="provider-name">{{ provider.name }}</div>
                <BarChartFixed 
                  :bars="generateBarsFromData(provider.data)"
                  :tooltip-style="tooltipStyle"
                />
              </div>
              
              <!-- Expand/Collapse Button -->
              <div v-if="wysistatDataIssuesProviders.length > 2" class="text-center mt-3">
                <v-btn
                  text
                  small
                  color="primary"
                  @click="expandedSections.wysistatDataIssues = !expandedSections.wysistatDataIssues"
                >
                  <v-icon left small>
                    {{ expandedSections.wysistatDataIssues ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                  </v-icon>
                  {{ expandedSections.wysistatDataIssues ? 'Voir moins' : `Voir ${wysistatDataIssuesProviders.length - 2} de plus` }}
                </v-btn>
              </div>
            </div>
            
            <!-- Loading State -->
            <div v-else-if="loading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-3 text--secondary">Chargement des données...</div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="text-center py-8 text--secondary">
              <v-icon large class="mb-3">mdi-check-circle</v-icon>
              <div>Aucun problème de remontée détecté</div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import HoverTooltip from '@/components/common/HoverTooltip.vue';
import BarChartFixed from '@/components/admin/data-availability/charts/BarChartFixed.vue';
import { formatNumberCompact } from '@/utils/formatting';
import { mdiArrowTopRight, mdiArrowBottomLeft } from '@mdi/js';

/**
 * Expected API data structure:
 * {
 *   stats: {
 *     views: {
 *       value: 1235,
 *       trend: 10,
 *       trendDirection: "up"
 *     },
 *     sessions: {
 *       value: 3000000,
 *       trend: 7,
 *       trendDirection: "up"
 *     }
 *   },
 *   audienceIssues: {
 *     gaDataDrop: [
 *       {
 *         id: 1,
 *         name: "Closer",
 *         data: [
 *           {
 *             date: "2024-01-01",
 *             value: 85,
 *             hasIssue: false
 *           },
 *           {
 *             date: "2024-01-02",
 *             value: 0,
 *             hasIssue: true
 *           }
 *         ]
 *       },
 *       {
 *         id: 2,
 *         name: "MarieFrance",
 *         data: [...]
 *       }
 *     ],
 *     wysistatDataDrop: [
 *       {
 *         id: 3,
 *         name: "Closer",
 *         data: [...]
 *       },
 *       {
 *         id: 4,
 *         name: "MarieFrance",
 *         data: [...]
 *       }
 *     ]
 *   }
 * }
 */
export default {
  name: 'AudienceOverviewCard',
  components: {
    HoverTooltip,
    BarChartFixed
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      icons: {
        mdiArrowTopRight,
        mdiArrowBottomLeft
      },
      tooltipStyle: {
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: '8px 12px',
        fontSize: '14px',
        color: '#333',
        whiteSpace: 'nowrap'
      },
      expandedSections: {
        gaDataIssues: false,
        wysistatDataIssues: false
      }
    };
  },
  methods: {
    generateBarsFromData(providerData) {
      // Convert API data to bar chart format
      if (!providerData || !Array.isArray(providerData)) {
        return this.generateDefaultBars(); // Fallback to default data
      }
      
      const bars = providerData.map((dataPoint, index) => {
        const isLastBar = index === providerData.length - 1;
        const hasIssue = dataPoint.hasIssue || dataPoint.value === 0;
        
        const value = dataPoint.value !== undefined ? dataPoint.value : 0;
        
        const bar = {
          height: this.normalizeHeight(value),
          tooltip: this.formatDataTooltip(dataPoint),
          blinking: isLastBar && hasIssue,
          color: hasIssue ? '#ff2000' : '#56ca00'
        };
        
        return bar;
      });
      
      return bars;
    },
    generateDefaultBars() {
      const bars = [];
      for (let i = 0; i < 30; i++) {
        bars.push({
          height: Math.floor(Math.random() * 60) + 30,
          tooltip: this.getDefaultTooltip(i),
          blinking: false,
          color: '#56ca00'
        });
      }
      return bars;
    },
    getDefaultTooltip(index) {
      const date = new Date();
      date.setDate(date.getDate() - (30 - index));
      const dateStr = date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
      return dateStr;
    },
    normalizeHeight(value) {
      // Handle zero values
      if (value === 0) {
        return 5; // Minimum visible height for zero values
      }
      
      // Use logarithmic scale for better visual distribution
      const logValue = Math.log10(value + 1);
      
      // Typical values might range from 0 to ~100
      const maxExpectedLog = Math.log10(101); // ≈ 2
      
      // Map to height percentage
      const minHeight = 15;
      const maxHeight = 85;
      
      const normalizedLog = Math.min(logValue / maxExpectedLog, 1);
      const height = minHeight + (normalizedLog * (maxHeight - minHeight));
      
      return Math.round(height);
    },
    formatDataTooltip(dataPoint) {
      const date = dataPoint.date ? new Date(dataPoint.date).toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit' 
      }) : 'N/A';
      
      if (dataPoint.value !== undefined) {
        return `${date} - Valeur: ${dataPoint.value}`;
      }
      
      return `${date}`;
    }
  },
  computed: {
    gaDataIssuesProviders() {
      // Get providers with GA data drop issues from API response
      if (this.data && this.data.audienceIssues && this.data.audienceIssues.gaDataDrop) {
        return this.data.audienceIssues.gaDataDrop;
      }
      // Default data for demo
      return [
        {
          id: 1,
          name: 'Closer',
          data: this.generateDefaultData()
        },
        {
          id: 2,
          name: 'MarieFrance',
          data: this.generateDefaultData()
        }
      ];
    },
    wysistatDataIssuesProviders() {
      // Get providers with Wysistat data drop issues from API response
      if (this.data && this.data.audienceIssues && this.data.audienceIssues.wysistatDataDrop) {
        return this.data.audienceIssues.wysistatDataDrop;
      }
      // Default data for demo
      return [
        {
          id: 3,
          name: 'Closer',
          data: this.generateDefaultData()
        },
        {
          id: 4,
          name: 'MarieFrance',
          data: this.generateDefaultData()
        }
      ];
    },
    visibleGADataIssuesProviders() {
      // Show only first 2 providers unless expanded
      if (this.expandedSections.gaDataIssues || this.gaDataIssuesProviders.length <= 2) {
        return this.gaDataIssuesProviders;
      }
      return this.gaDataIssuesProviders.slice(0, 2);
    },
    visibleWysistatDataIssuesProviders() {
      // Show only first 2 providers unless expanded
      if (this.expandedSections.wysistatDataIssues || this.wysistatDataIssuesProviders.length <= 2) {
        return this.wysistatDataIssuesProviders;
      }
      return this.wysistatDataIssuesProviders.slice(0, 2);
    },
    // Header stats computed properties
    formatDataUploaded() {
      const value = this.data?.stats?.views?.value || 1235;
      return formatNumberCompact(value, 0);
    },
    dataUploadedTrend() {
      return this.data?.stats?.views?.trend || 10;
    },
    dataUploadedTrendColor() {
      if (this.dataUploadedTrend === null) return 'grey';
      return this.dataUploadedTrend >= 0 ? 'success' : 'error';
    },
    dataUploadedTrendIcon() {
      if (this.dataUploadedTrend === null) return '';
      const direction = this.data?.stats?.views?.trendDirection || (this.dataUploadedTrend >= 0 ? 'up' : 'down');
      return direction === 'up' ? this.icons.mdiArrowTopRight : this.icons.mdiArrowBottomLeft;
    },
    formatSessions() {
      const value = this.data?.stats?.sessions?.value || 3000000;
      return formatNumberCompact(value, 1);
    },
    sessionsTrend() {
      return this.data?.stats?.sessions?.trend || 7;
    },
    sessionsTrendColor() {
      if (this.sessionsTrend === null) return 'grey';
      return this.sessionsTrend >= 0 ? 'success' : 'error';
    },
    sessionsTrendIcon() {
      if (this.sessionsTrend === null) return '';
      const direction = this.data?.stats?.sessions?.trendDirection || (this.sessionsTrend >= 0 ? 'up' : 'down');
      return direction === 'up' ? this.icons.mdiArrowTopRight : this.icons.mdiArrowBottomLeft;
    },
    generateDefaultData() {
      const data = [];
      for (let i = 0; i < 30; i++) {
        const date = new Date();
        date.setDate(date.getDate() - (30 - i));
        data.push({
          date: date.toISOString().split('T')[0],
          value: Math.floor(Math.random() * 100),
          hasIssue: false
        });
      }
      return data;
    }
  }
};
</script>

<style scoped>
/* Stat cards with modern styling */
.stat-card {
  background-color: #f4f5fa;
  padding: 20px 24px;
  border-radius: 8px;
  transition: all 0.3s ease;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e0e6ee;
  box-shadow: 0 0 0px #e0e0e0;
  min-height: 100px;
  cursor: pointer;
}

.stat-card:hover {
  background-color: #f4f6ff;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

.stat-card:active {
  background-color: #f4f5fa;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.15);
}

.stat-left {
  flex-shrink: 0;
  min-width: 80px;
}

.stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #666;
  line-height: 1.3;
}

.stat-center {
  flex: 1;
  text-align: center;
  padding: 0 16px;
}

.stat-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-value {
  color: #1a1a1a;
  margin-bottom: 4px;
}

.trend-value {
  font-size: 14px;
  font-weight: 600;
  color: #2e7d32;
}

/* Chart cards styling */
.chart-card {
  border: 1px solid #e0e6ee;
  background-color: #ffffff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 0 0px #e0e0e0;
  height: 100%;
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
}

/* Provider chart items */
.provider-chart-item {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f4f5fa;
  border-radius: 8px;
  transition: all 0.3s ease;
  border: 1px solid #e0e6ee;
  box-shadow: 0 0 0px #e0e0e0;
}

.provider-chart-item:hover {
  background-color: #f4f6ff;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
}

.provider-chart-item:last-child {
  margin-bottom: 0;
}

.chart-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.provider-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #565656;
  margin-bottom: 12px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .stat-card {
    margin-bottom: 16px;
  }
  
  .chart-card {
    margin-bottom: 16px;
  }
}
</style>